import { React, useContext, useEffect, useRef, useState } from "react";
import "./Profile.css";
import { PenLine, LogOut, Lock, Unlock } from "lucide-react";
import ProfileStats from "./ProfileStats";
import ProfileMatches from "./ProfileMatches";
import ProfilePosts from "./ProfilePosts";
import IsAuth from "../../createContext/is-Auth/IsAuthContext";
import ProfilePhotos from "./ProfilePhotos";
import ProfileReels from "./ProfileReels";
import { Link, useParams } from "react-router-dom";
import { ProfileContext } from "../../createContext/Profile/ProfileContext";
import { PostContext } from "../../createContext/Post/PostContext";
import TextareaAutosize from "react-textarea-autosize";
import { useLocation } from "react-router-dom";
import ProfilePassionsGrid from "./Components/ProfilePassionsGrid";
import IndustryDropdown from "../RightSidebar/IndustryDropdown";
import ProfileBadges from "./Components/ProfileBadges";

const LoadingSpinner = () => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white/1">
      <div className="relative w-16 h-16">
        {/* Outer circle */}
        <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />

        {/* Spinning circle */}
        <div className="absolute inset-0 border-4 border-transparent border-t-black rounded-full animate-spin" />
      </div>
    </div>
  );
};

const tabs = ["Overview", "Photos", "Post", "Reels", "Matches"];


export default function Profile() {
  const [showPassions, setShowPassions] = useState(true);
  const [activeTab, setActiveTab] = useState("Overview");
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useContext(IsAuth);
  const { username } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [followStatus, setFollowStatus] = useState('none');
  const {
    getProfile,
    profile,
    toggleFollow,
    setProfile,
    userPosts = [],
    getUserPosts,
    updateUser,
  } = useContext(ProfileContext);

  const { Myposts, getPosts } = useContext(PostContext);
  const [editedProfile, setEditedProfile] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [profileFollowers, setProfileFollowers] = useState(0);
  const fileInputRef = useRef(null);
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.reload();
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setEditedProfile((prev) => ({
          ...prev,
          media: file,
          avatar: URL.createObjectURL(file),
        }));
      } catch (error) {
        console.error("Avatar upload failed", error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setEditedProfile((prev) => ({ ...prev, [name]: inputValue }));
  };

  // Reset profile state when navigating between profiles
  useEffect(() => {
    setProfile(null); // Reset profile when location changes
    return () => {
      setProfile(null); // Cleanup on unmount
    };
  }, [location.pathname]);


  // Main data fetching effect
  useEffect(() => {
    const fetcher = async () => {
      setIsLoading(true);
      try {
        if (username) {
          await getProfile(username);
          await getUserPosts(username);
        } else {
          await getProfile(null);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetcher();

    // Reset states when route changes
    setShowPassions(true);
    setActiveTab("Overview");
    setIsEditing(false);
    setEditedProfile({});

  }, [username]);

  useEffect(()=>{
    const PostFetcher = async() => {
      if (!username && user && Myposts.length === 0) {
        await getPosts(true);
      }
    };
    PostFetcher();
  },[user]);

  useEffect(() => {
    if (profile) {
      setProfileFollowers(profile.followers);
    }
  }, [profile]);


  // Handle follow status updates
  useEffect(() => {
    if (!user || !profile) return; // Prevent accessing undefined properties
    if (user.following?.some((item) => item._id === profile._id)) {
      setFollowStatus("following");
    } else if (profile.followRequests?.includes(user._id)) {
      setFollowStatus("requested");
    } else {
      setFollowStatus("none");
    }
  }, [user?.following, profile?._id, profile?.followRequests]); // Updated dependencies


  const handleFollow = async () => {
    try {
      if (followStatus === 'following') {
        await toggleFollow(profile._id);
        setFollowStatus('none');
        setProfileFollowers(prev => prev - 1);
      } else {
        const response = await toggleFollow(profile._id);
        if (response.data.status === 'followed') {
          setFollowStatus('following');
          setProfileFollowers(prev => prev + 1);
        } else if (response.data.status === 'requested' || response.data.status === 'pending') {
          setFollowStatus('requested');
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const formData = new FormData();
      Object.entries(editedProfile).forEach(([key, value]) => {
        formData.append(key, value);
      });
      await updateUser(formData);
      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      console.error("Profile update failed", error);
    }
  };

  if (isLoading) {
    return (
      <div className="ProfileContainer">
        <LoadingSpinner />
      </div>
    );
  }

  if (username && !profile) {
    return (
      <div className="ProfileContainer">
        No Profile with given username found .
      </div>
    );
  }

  const renderFollowButton = () => {
    switch (followStatus) {
      case 'following':
        return (
          <button
            className="followButton"
            onClick={handleFollow}
          >
            Unfollow
          </button>
        );
      case 'requested':
        return (
          <button
            className="followButton bg-gray-200 text-black"
            disabled
          >
            Requested
          </button>
        );
      default:
        return (
          <button
            className="followButton"
            onClick={handleFollow}
          >
            {profile?.isPrivate ? 'Request to Follow' : 'Follow'}
          </button>
        );
    }
  };

  const currentUser = username ? profile : user;

  const handleEditPassionsClick = () => {
    if (user?.passions) {
      localStorage.setItem("userPassions", JSON.stringify(user.passions));
    }
    localStorage.setItem("isEditing", "true");
  };

  return (
    <div className="ProfileContainer">
      {isEditing ? (
        <div className="ProfileHeader">
          @
          <input
            name="userName"
            value={editedProfile.userName || currentUser?.userName}
            onChange={handleInputChange}
          />
        </div>
      ) : (
        <div className="ProfileHeader flex justify-between items-center">
          <div
            onClick={() => {
              if (isEditing) {
                setEditingField("userName");
              }
            }}
          >
            @{currentUser?.userName}
          </div>
          {!username && <button
            onClick={handleLogout}
            className="px-3 py-1 text-sm rounded-lg hover:bg-gray-200 flex items-center gap-2 bg-gray-300"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>}
        </div>
      )}
      <div className="profileBox">
        <div className="profile-main">
          {isEditing ? (
            <div className="profile-image">
              <div className="profile-avatar-edit-container">
                <img
                  src={editedProfile.avatar || currentUser?.avatar}
                  alt="Profile"
                  className="profile-avatar"
                />
                <div
                  className="avatar-overlay"
                  onClick={() => fileInputRef.current.click()}
                >
                  Upload
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleAvatarUpload}
                />
              </div>
            </div>
          ) : (
            <div className="profile-image">
              <img
                src={currentUser?.avatar}
                alt="Profile"
                className="profile-avatar"
              />
            </div>
          )}

          <div className="profile-info">
            {isEditing ? (
              <div className="profile-name">
                <input
                  name="name"
                  value={editedProfile.name || currentUser?.name}
                  onChange={handleInputChange}
                />
              </div>
            ) : (
              <h1
                className="profile-name"
                onClick={() => {
                  if (isEditing) {
                    setEditingField("name");
                  }
                }}
              >
                {currentUser?.name}
              </h1>
            )}
            <div className="profile-metrics">
              <div className="metric">
                <span className="metric-value">
                  {username
                    ? (userPosts
                      ? userPosts.length
                      : 0)
                    : (Myposts
                      ? Myposts.length
                      : 0)}
                </span>
                <span className="metric-label">posts</span>
              </div>
              <div className="metric">
                {username ? (
                  <>
                    <span className="metric-value">
                      {profileFollowers ? profileFollowers : 0}
                    </span>
                    <span className="metric-label"> followers</span>
                  </>
                ) : (
                  <Link to={`/list/followers`}>
                    <span className="metric-value">
                      {user ? user.followers.length : 0}
                    </span>
                    <span className="metric-label"> followers</span>
                  </Link>
                )}
              </div>
              <div className="metric">
                {username ? (
                  <>
                    <span className="metric-value link">
                      {profile ? profile.following : 0}
                    </span>
                    <span className="metric-label link"> following</span>
                  </>
                ) : (
                  <Link to={`/list/following`}>
                    <span className="metric-value link">
                      {user ? user.following.length : 0}
                    </span>
                    <span className="metric-label link"> following</span>
                  </Link>
                )}
              </div>
            </div>

            {isEditing ? (
              <TextareaAutosize
                name="bio"
                className="profile-bio"
                value={editedProfile.bio || currentUser?.bio}
                minRows={2}
                maxRows={15}
                maxLength={600}
                onChange={handleInputChange}
              />
            ) : (
              <p className="profile-bio text-wrap">{currentUser?.bio}</p>
            )}

            <div className="profile-divider">
              <div>
                <div className="flex gap-2">
                  <div className="profile-location">
                    <span className="location-icon">📍</span>
                    {isEditing ? (
                      <input
                        name="location"
                        value={editedProfile.location || currentUser?.location}
                        onChange={handleInputChange}
                        className="w-28"
                      />
                    ) : (
                      <span
                        onClick={() => {
                          if (isEditing) {
                            setEditingField("location");
                          }
                        }}
                      >
                        {currentUser?.location}
                      </span>
                    )}
                    <span className="location-icon">🏭</span>
                    {isEditing ? (
                      <IndustryDropdown
                        value={editedProfile.industry || currentUser?.industry}
                        onChange={(value) => handleInputChange({
                          target: { name: 'industry', value }
                        })}
                      />
                    ) : (
                      <span
                        onClick={() => {
                          if (isEditing) {
                            setEditingField("industry");
                          }
                        }}
                      >
                        {currentUser?.industry}
                      </span>
                    )}
                  </div>
                </div>

                {/* Add Privacy Toggle */}
                {isEditing && (
                  <div className="flex items-center gap-2 mt-3 justify-center md:justify-start">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isPrivate"
                        checked={editedProfile.isPrivate ?? currentUser?.isPrivate}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        {editedProfile.isPrivate ?? currentUser?.isPrivate ? (
                          <>
                            <Lock className="w-4 h-4" />
                            Private Account
                          </>
                        ) : (
                          <>
                            <Unlock className="w-4 h-4" />
                            Public Account
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                {/* Show privacy status when not editing */}
                {!isEditing && (
                  <div className="flex items-center text-center justify-center md:justify-start gap-1 mt-2 ml-1 text-sm text-gray-600">
                    {currentUser?.isPrivate ? (
                      <>
                        <Lock className="w-4 h-4" />
                        Private Account
                      </>
                    ) : (
                      <>
                        <Unlock className="w-4 h-4" />
                        Public Account
                      </>
                    )}
                  </div>
                )}



                {username && profile?.followedBy?.length > 0 && (
                  <div className="profile-followers">
                    <span>Followed by</span>
                    <div className="follower-avatars">
                      {profile.followedBy.map((_f) => (
                        <img src={_f.avatar} alt="followedBy" />
                      ))}
                    </div>
                    <span className="follower-names">
                      {profile.followedBy.map((follower, index) => (
                        <span key={follower.id}>
                          {follower.name.split(" ")[0].toLowerCase()}
                          {index < profile.followedBy.length - 1 ? "," : " ..."}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
              {username && user && profile && user.userName !== profile.userName && (
                renderFollowButton()
              )}
              {!username && !isEditing && (
                <button
                  className="followButton edit-profile-button"
                  onClick={() => { setIsEditing(true); setShowPassions(true) }}
                >
                  Edit Profile
                </button>
              )}
              {isEditing && (
                <div className="edit-profile-button">
                  <button className="followButton" onClick={handleSaveProfile}>
                    Save
                  </button>
                  <button
                    className="followButton-cancel"
                    onClick={() => {
                      setEditingField("");
                      setIsEditing(false);
                      setEditedProfile({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="profile-badges-section mt-4 mb-2">
          <ProfileBadges progress={currentUser?.progress?.overallScore} ShowViewMore={username ? false : true}/>
        </div>

        <div className="profile-passions">
          <div className="flex justify-between items-center">
            {/* {!isEditing && <span
              className="show-passions-toggle"
              onClick={() => setShowPassions(!showPassions)}
            >
              {showPassions ? "▼" : ">"} Show Passions
            </span>} */}
            {!username && (
              <a href="/register/passion" onClick={() => localStorage.setItem("isEditing", true)}>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1" onClick={handleEditPassionsClick}
                >
                  <PenLine className="w-4 h-4" />
                  Edit Passions
                </button>
              </a>
            )}
          </div>

          <ProfilePassionsGrid
            passions={username ? profile?.passions : user?.passions}
            showPassions={showPassions}
          />
        </div>


      </div>
      {(!username|| profile?.isPrivate === false || followStatus === "following") &&
        <>
          <div className="ProfileNav">
            {tabs.map((tab) => (
              <p
                key={tab}
                className={`ProfileTab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </p>
            ))}
          </div>
          <div className="p-4">
            {activeTab === "Overview" && (
              <ProfileStats progress={currentUser?.progress} />
            )}
            {activeTab === "Photos" && <ProfilePhotos />}
            {activeTab === "Post" && <ProfilePosts />}
            {activeTab === "Reels" && <ProfileReels />}
            {activeTab === "Matches" && <ProfileMatches username={username} />}
          </div>
        </>}
    </div>
  );
}
